import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Briefcase, Eye, EyeOff, Loader2, Trash2, RefreshCw, Download } from "lucide-react";
import { usePortalAuth } from "../../lib/portalAuth";
import { OW_API } from "../../lib/operatorWalk";
import { useAdminBadges } from "../../lib/adminBadges";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// type: "contact" | "careers"
export default function SubmissionsTable({ type }) {
  const { authHeader } = usePortalAuth();
  const { adjustBadge } = useAdminBadges();
  const isContact = type === "contact";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${OW_API}/admin/submissions`, { headers: authHeader, params: { type } });
      setRows(res.data.submissions || []);
    } catch {
      toast.error("Could not load submissions.");
    } finally {
      setLoading(false);
    }
  }, [authHeader, type]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleRead = async (s) => {
    setBusyId(s.id);
    try {
      await axios.patch(`${OW_API}/admin/submissions/${s.id}`, { read: !s.read }, { headers: authHeader });
      setRows((r) => r.map((x) => (x.id === s.id ? { ...x, read: !s.read } : x)));
      // Marking unread -> read ticks the sidebar badge down; the reverse ticks it up.
      adjustBadge(type, s.read ? 1 : -1);
    } catch {
      toast.error("Failed to update.");
    } finally {
      setBusyId(null);
    }
  };

  const downloadResume = async (s) => {
    setBusyId(s.id);
    try {
      const res = await axios.get(`${OW_API}/admin/submissions/${s.id}/resume`, {
        headers: authHeader,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = s.resume?.filename || "resume";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download resume.");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    setBusyId(id);
    const target = rows.find((x) => x.id === id);
    try {
      await axios.delete(`${OW_API}/admin/submissions/${id}`, { headers: authHeader });
      setRows((r) => r.filter((x) => x.id !== id));
      if (target && !target.read) adjustBadge(type, -1);
      toast.success("Deleted.");
    } catch {
      toast.error("Failed to delete.");
    } finally {
      setBusyId(null);
      setConfirmDeleteId(null);
    }
  };

  return (
    <div data-testid={`submissions-table-${type}`}>
      <div className="flex justify-end mb-4">
        <button
          onClick={load}
          className="inline-flex items-center gap-2 text-sm text-brand-700 hover:bg-brand-100 px-3 py-2 transition-colors"
          data-testid="submissions-refresh"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>
      {loading ? (
        <div className="py-20 flex items-center justify-center text-ink-muted">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading
        </div>
      ) : rows.length === 0 ? (
        <div className="py-20 text-center border border-ink-line bg-white" data-testid="submissions-empty">
          <div className="font-serif text-2xl text-brand-700">No {isContact ? "contact" : "career"} submissions yet.</div>
          <p className="mt-3 text-ink-muted">Inquiries from the {isContact ? "contact" : "careers"} form will appear here.</p>
        </div>
      ) : (
        <div className="border border-ink-line bg-white" data-testid="submissions-list">
          {rows.map((s) => {
            const isOpen = expandedId === s.id;
            return (
              <div
                key={s.id}
                className={`border-b border-ink-line last:border-b-0 ${!s.read ? "bg-brand-50" : "bg-white"}`}
                data-testid={`submission-row-${s.id}`}
              >
                <button
                  type="button"
                  onClick={() => setExpandedId(isOpen ? null : s.id)}
                  className="w-full text-left px-6 py-5 flex items-start gap-4 hover:bg-brand-100/40 transition-colors"
                  data-testid={`submission-toggle-${s.id}`}
                >
                  <div className="shrink-0 mt-1">
                    {isContact ? <Mail className="w-5 h-5 text-brand-700" /> : <Briefcase className="w-5 h-5 text-brand-700" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <span className="font-medium text-brand-700">{s.name}</span>
                      {!s.read && <span className="inline-block w-2 h-2 rounded-full bg-brand-400" aria-label="unread" />}
                    </div>
                    <div className="text-sm text-ink-soft mt-1 truncate">
                      {s.email} {s.company ? `· ${s.company}` : s.background ? `· ${s.background}` : ""}
                    </div>
                    <div className="text-xs text-ink-muted mt-1">{formatDate(s.created_at)}</div>
                  </div>
                </button>

                {isOpen && (
                  <div className="px-6 pb-6 pt-2 border-t border-ink-line bg-white" data-testid={`submission-detail-${s.id}`}>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                      <dl className="md:col-span-4 space-y-3 text-sm">
                        <div>
                          <dt className="text-[10px] uppercase tracking-[0.2em] text-brand-500">Email</dt>
                          <dd className="text-ink-soft break-all">
                            <a className="hover:text-brand-700" href={`mailto:${s.email}`}>{s.email}</a>
                          </dd>
                        </div>
                        <div>
                          <dt className="text-[10px] uppercase tracking-[0.2em] text-brand-500">{isContact ? "Company" : "Background"}</dt>
                          <dd className="text-ink-soft">{(isContact ? s.company : s.background) || "Not provided"}</dd>
                        </div>
                        <div>
                          <dt className="text-[10px] uppercase tracking-[0.2em] text-brand-500">Submitted</dt>
                          <dd className="text-ink-soft">{formatDate(s.created_at)}</dd>
                        </div>
                        {!isContact && (
                          <div>
                            <dt className="text-[10px] uppercase tracking-[0.2em] text-brand-500">Resume</dt>
                            <dd className="text-ink-soft">
                              {s.resume ? (
                                <button
                                  onClick={() => downloadResume(s)}
                                  disabled={busyId === s.id}
                                  className="inline-flex items-center gap-2 text-brand-700 hover:text-brand-500 font-medium transition-colors disabled:opacity-60"
                                  data-testid={`submission-resume-download-${s.id}`}
                                >
                                  {busyId === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                  <span className="break-all text-left">{s.resume.filename}</span>
                                </button>
                              ) : (
                                "Not attached"
                              )}
                            </dd>
                          </div>
                        )}
                      </dl>
                      <div className="md:col-span-8">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-brand-500 mb-2">Message</div>
                        <div className="bg-brand-100/60 border border-brand-200 p-5 whitespace-pre-wrap text-ink-soft leading-relaxed">
                          {s.message}
                        </div>
                        <div className="mt-6 flex flex-wrap items-center gap-3">
                          <a
                            href={`mailto:${s.email}?subject=Re:%20your%20inquiry`}
                            className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-5 py-3 text-sm font-medium transition-colors"
                            data-testid={`submission-reply-${s.id}`}
                          >
                            Reply by Email <Mail className="w-4 h-4" />
                          </a>
                          <button
                            onClick={() => toggleRead(s)}
                            disabled={busyId === s.id}
                            className="inline-flex items-center gap-2 border border-brand-700 text-brand-700 hover:bg-brand-100 px-5 py-3 text-sm font-medium transition-colors disabled:opacity-60"
                            data-testid={`submission-toggle-read-${s.id}`}
                          >
                            {s.read ? <><EyeOff className="w-4 h-4" /> Mark as Unread</> : <><Eye className="w-4 h-4" /> Mark as Read</>}
                          </button>
                          {confirmDeleteId === s.id ? (
                            <div className="inline-flex items-center gap-2">
                              <span className="text-sm text-ink-soft">Are you sure?</span>
                              <button
                                onClick={() => remove(s.id)}
                                disabled={busyId === s.id}
                                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60"
                                data-testid={`submission-confirm-delete-${s.id}`}
                              >
                                {busyId === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                Yes, delete
                              </button>
                              <button onClick={() => setConfirmDeleteId(null)} className="text-sm text-ink-muted hover:text-brand-700 px-3 py-2">
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setConfirmDeleteId(s.id)}
                              className="inline-flex items-center gap-2 text-red-600 hover:bg-red-50 border border-red-200 px-5 py-3 text-sm font-medium transition-colors"
                              data-testid={`submission-delete-${s.id}`}
                            >
                              <Trash2 className="w-4 h-4" /> Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
